import { useEffect, useRef, useState } from "react";

export type ResponsiveTileTypographyTier =
  | "compact"
  | "default"
  | "comfortable";

const COMPACT_MAX_WIDTH = 520;
const COMPACT_MAX_HEIGHT = 360;
const COMFORTABLE_MIN_WIDTH = 820;
const COMFORTABLE_MIN_HEIGHT = 560;

// A tier only changes once the tile is this far past a threshold. Each tier
// changes padding and font size, which can nudge the measured size by a few
// pixels; without this band a tile sitting on a threshold flips back and forth.
const HYSTERESIS_PX = 24;

const getResponsiveTileTypographyTier = (
  width: number,
  height: number,
  previous: ResponsiveTileTypographyTier
): ResponsiveTileTypographyTier => {
  if (!width || !height) {
    return previous;
  }

  // Leaving a tier requires crossing its threshold by the full band; entering
  // uses the plain threshold.
  const compactSlack = previous === "compact" ? HYSTERESIS_PX : 0;
  if (
    width <= COMPACT_MAX_WIDTH + compactSlack ||
    height <= COMPACT_MAX_HEIGHT + compactSlack
  ) {
    return "compact";
  }

  const comfortableSlack = previous === "comfortable" ? HYSTERESIS_PX : 0;
  if (
    width >= COMFORTABLE_MIN_WIDTH - comfortableSlack &&
    height >= COMFORTABLE_MIN_HEIGHT - comfortableSlack
  ) {
    return "comfortable";
  }

  return "default";
};

export const useResponsiveTileTypography = <T extends HTMLElement>() => {
  const containerRef = useRef<T | null>(null);
  const [state, setState] = useState<{
    width: number;
    height: number;
    tier: ResponsiveTileTypographyTier;
  }>({ width: 0, height: 0, tier: "default" });

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }

    // Measure the border box, not the content box: consumers change their
    // padding per tier, so the content box would shrink or grow as a result
    // of the tier switch itself and feed back into the next measurement.
    const updateDimensions = () => {
      const width = Math.round(node.offsetWidth);
      const height = Math.round(node.offsetHeight);

      setState((previous) => {
        const tier = getResponsiveTileTypographyTier(
          width,
          height,
          previous.tier
        );
        return previous.width === width &&
          previous.height === height &&
          previous.tier === tier
          ? previous
          : { width, height, tier };
      });
    };

    updateDimensions();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(node, { box: "border-box" });

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return {
    containerRef,
    width: state.width,
    height: state.height,
    tier: state.tier,
    isCompact: state.tier === "compact",
    isComfortable: state.tier === "comfortable",
  };
};
